import { JSX, useCallback, useMemo } from 'react';
import { Box, Grow, Popover, Typography } from '@mui/material';
import { format, parseISO } from 'date-fns';
import { ScalarLineChart } from 'components/Charts';
import { ChartSeries } from 'components/Charts/types';
import { useChartData } from './ChartDataContext';
import { ChartPopoverProps } from './ChartPopover.props';
import { buildSeriesPoints, formatMetricValue } from './chartPopoverData';

const DEFAULT_COLOR = '#1A64B1';

const ChartPopover = ({ open, anchorEl, onClose, title, color, entityName, metricKey, cellFormat }: ChartPopoverProps) => {
  const seriesColor = color ?? DEFAULT_COLOR;
  const { chartData, isLoading, selectedDate } = useChartData();

  const series: ChartSeries[] = useMemo(() => {
    if (!chartData || !entityName || !metricKey) {
      return [];
    }

    const data = buildSeriesPoints(chartData, entityName, metricKey);
    if (data.length === 0) {
      return [];
    }

    const selectedPoint = selectedDate ? data.find(p => p.x === selectedDate) : undefined;

    const seriesConfig: Record<string, unknown> = {};
    if (selectedPoint) {
      seriesConfig.markPoint = {
        data: [{ coord: [selectedPoint.x, selectedPoint.y] }],
        symbol: 'circle',
        symbolSize: 14,
        itemStyle: {
          color: '#fff',
          borderColor: seriesColor,
          borderWidth: 3,
          shadowBlur: 8,
          shadowColor: `${seriesColor}80`,
        },
        label: { show: false },
      };
    }

    return [{ id: `${entityName}-${metricKey}`, label: title ?? metricKey, data, color: seriesColor, config: seriesConfig }];
  }, [chartData, entityName, metricKey, seriesColor, title, selectedDate]);

  const hasData = series.length > 0 && series[0].data.length > 0;

  const formatDate = useCallback((value: string | number) => {
    try {
      // Time-axis ticks arrive as ms timestamps (UTC midnight for date strings):
      // read the UTC calendar date so labels never shift a day by timezone.
      const isoDate = typeof value === 'number' ? new Date(value).toISOString().slice(0, 10) : String(value);
      return format(parseISO(isoDate), 'MM/dd/yyyy');
    } catch {
      return String(value);
    }
  }, []);

  const formatValue = useCallback(
    (value: number | null | undefined) => formatMetricValue(value, cellFormat),
    [cellFormat]
  );

  const tooltipFormatter = useCallback(
    (params: { axisValue?: string | number; marker?: string; seriesName?: string; value?: number | (string | number)[] }[]) => {
      if (!Array.isArray(params) || params.length === 0) return '';
      const { axisValue, marker, seriesName, value } = params[0];
      // Time-axis series data is [date, value] pairs: read both parts from the pair
      const xValue = Array.isArray(value) ? value[0] : axisValue;
      const yValue = Array.isArray(value) ? (value[1] as number) : value;
      const formattedDate = formatDate(xValue ?? '');
      return `${formattedDate}<br/>${marker ?? ''} ${seriesName ?? ''}: <b>${formatValue(yValue)}</b>`;
    },
    [formatDate, formatValue]
  );

  let chartBody: JSX.Element;
  if (isLoading) {
    chartBody = (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
        <Typography variant="body2" color="text.secondary">Loading chart data...</Typography>
      </Box>
    );
  } else if (hasData) {
    chartBody = (
      <ScalarLineChart
        series={series}
        area
        showPoints
        smooth
        config={{
          tooltip: { show: true },
          legend: { show: false },
          xAxis: { formatter: formatDate, type: 'time' },
          yAxis: { formatter: (value: string | number) => formatValue(typeof value === 'number' ? value : Number(value)) },
          options: {
            tooltip: {
              show: true,
              trigger: 'axis',
              formatter: tooltipFormatter,
              backgroundColor: 'rgba(255, 255, 255, 0.85)',
              borderColor: 'rgba(82, 163, 255, 0.3)',
              borderWidth: 1,
              borderRadius: 12,
              padding: [12, 16],
              textStyle: { color: '#082444', fontSize: 13 },
              extraCssText: 'backdrop-filter: blur(12px); box-shadow: 0 8px 32px rgba(26, 100, 177, 0.15);',
            },
          },
        }}
        style={{ width: '100%', height: '100%' }}
      />
    );
  } else {
    chartBody = (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
        <Typography variant="body2" color="text.secondary">No trending data available</Typography>
      </Box>
    );
  }

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'left',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'left',
      }}
      disableRestoreFocus
      TransitionComponent={Grow}
      TransitionProps={{
        timeout: { enter: 350, exit: 200 },
      }}
      slotProps={{
        paper: {
          sx: {
            width: 440,
            borderRadius: 3,
            overflow: 'hidden',
            background:
              'linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(242,248,255,0.95) 100%)',
            backdropFilter: 'blur(20px)',
            border: `1px solid ${seriesColor}26`,
            boxShadow: `0 20px 60px ${seriesColor}26, 0 1px 3px rgba(0,0,0,0.08)`,
          },
        },
      }}>
      <Box
        sx={{
          background: `linear-gradient(90deg, ${seriesColor} 0%, ${seriesColor}99 50%, ${seriesColor}44 100%)`,
          height: 3,
          width: '100%',
        }}
      />
      {title && (
        <Typography
          variant="subtitle2"
          sx={{
            mb: 1,
            px: 2.5,
            pt: 2,
            fontWeight: 700,
            color: '#082444',
            letterSpacing: '-0.01em',
          }}>
          {title}
        </Typography>
      )}
      <Box sx={{ width: '100%', height: 260, px: 1, pb: 2 }}>
        {chartBody}
      </Box>
    </Popover>
  );
};

export default ChartPopover;
